import { redirect, type LoaderFunctionArgs } from 'react-router';

type LegacyKind = 'lighthouse' | 'relay';

/**
 * Rewrite a legacy `/lighthouses/*` or `/relays/*` URL to its `/hosts` equivalent, preserving the
 * query string:
 *
 * - the list becomes the type-filtered hosts list (`/hosts?filter.type=<kind>`)
 * - `add` becomes the kind-preselected add page (`/hosts/add?type=<kind>`)
 * - everything else (`:hostId`, `:hostId/edit`, …) maps straight across, since the host routes
 *   already serve every kind
 *
 * The `add` comparison is case-folded because route matching is case-insensitive — `/Lighthouses/aDd`
 * reached the add page before the merge, so it has to keep doing so. Host IDs pass through untouched.
 */
export function legacyHostsUrl(kind: LegacyKind, splat: string, search: string): string {
  const params = new URLSearchParams(search);
  const path = splat.replace(/\/+$/, '');
  if (path === '') {
    params.set('filter.type', kind);
    return `/hosts?${params.toString()}`;
  }
  if (path.toLowerCase() === 'add') {
    params.set('type', kind);
    return `/hosts/add?${params.toString()}`;
  }
  const query = params.toString();
  return `/hosts/${path}${query ? `?${query}` : ''}`;
}

/**
 * The splat comes from the router's own match rather than being re-parsed from the URL, so
 * case-variant prefixes (`/Lighthouses/…`) arrive already stripped.
 */
export function legacyHostsLoader(kind: LegacyKind) {
  return ({ request, params }: LoaderFunctionArgs) =>
    redirect(legacyHostsUrl(kind, params['*'] ?? '', new URL(request.url).search));
}
